import { useState, useCallback, useEffect, useRef } from "react";
import { Monitor, Smartphone, RefreshCw, Eye, MessageSquare } from "lucide-react";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WIDTHS = { desktop: 1280, phone: 414 };

export const PagePreviewCard = ({ slug, version = 0 }) => {
  const [device, setDevice] = useState("desktop");
  const [nonce, setNonce] = useState(0);
  const [scale, setScale] = useState(0.3);
  const wrapRef = useRef(null);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  const width = WIDTHS[device];
  const height = device === "phone" ? 820 : 760;

  useEffect(() => {
    const measure = () => {
      if (wrapRef.current) setScale(wrapRef.current.clientWidth / width);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [width]);

  if (!slug) return null;

  return (
    <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-6" data-testid="mlo-page-preview">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
            <Eye size={13} /> Live preview
          </div>
          <h3 className="mt-1 font-heading font-medium text-xl text-navy">How your page looks right now</h3>
          <p className="mt-1 text-sm text-ink-500">
            This is the real page, not a mock-up. Save an edit above and hit refresh to see it land.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="flex rounded-full bg-linen p-0.5">
            <button
              type="button"
              onClick={() => setDevice("desktop")}
              data-testid="mlo-preview-desktop"
              aria-label="Desktop preview"
              className={`p-1.5 rounded-full transition-colors ${device === "desktop" ? "bg-white shadow-sm text-navy" : "text-ink-400 hover:text-navy"}`}
            >
              <Monitor size={14} />
            </button>
            <button
              type="button"
              onClick={() => setDevice("phone")}
              data-testid="mlo-preview-phone"
              aria-label="Phone preview"
              className={`p-1.5 rounded-full transition-colors ${device === "phone" ? "bg-white shadow-sm text-navy" : "text-ink-400 hover:text-navy"}`}
            >
              <Smartphone size={14} />
            </button>
          </div>
          <button
            type="button"
            onClick={refresh}
            data-testid="mlo-preview-refresh"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-linen hover:bg-linen-border text-navy"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      <div
        ref={wrapRef}
        className={`mt-5 rounded-2xl border border-linen-border bg-linen overflow-hidden relative ${
          device === "phone" ? "mx-auto max-w-[280px]" : ""
        }`}
        style={{ aspectRatio: device === "phone" ? "414 / 820" : "1280 / 760" }}
        data-testid="mlo-preview-frame"
      >
        <iframe
          key={`${device}-${version}-${nonce}`}
          title="Your page preview"
          src={`/${slug}`}
          style={{
            width: `${width}px`,
            height: `${height}px`,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
          className="absolute top-0 left-0 border-0 pointer-events-none"
          sandbox="allow-same-origin allow-scripts"
          loading="lazy"
        />
      </div>

      <div className="mt-6 pt-5 border-t border-linen-border">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
          <MessageSquare size={13} /> When you text the link
        </div>
        <p className="mt-1 text-sm text-ink-500">
          This card is what unfurls in a text or a post. It rebuilds a minute or two after you change your
          name, photo or headline.
        </p>
        <img
          key={`og-${version}-${nonce}`}
          src={`${API_ROOT}/og/mlo/${slug}.jpg?v=${version}-${nonce}`}
          alt="Link preview card"
          data-testid="mlo-preview-og"
          className="mt-3 w-full max-w-md rounded-xl border border-linen-border"
        />
      </div>
    </div>
  );
};

export default PagePreviewCard;
